import * as React from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { TableHead, Tooltip } from '@mui/material'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { capitalize } from '../../../../utils/functions'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import { CuratorStudentAnalysisState } from '../../../../features/types'

interface Props {
    studentsData: CuratorStudentAnalysisState[]
    pageNumber: number
    totalCount: number
    page: number
    rowsPerPage: number
    isFetching: boolean
    setPage: (page: number) => void
    setRowsPerPage: (rowsPerPage: number) => void
}

const AnalysisTable: React.FC<Props> = ({
    studentsData = [],
    pageNumber = 0,
    totalCount = 0,
    page = 0,
    rowsPerPage = 0,
    isFetching,
    setPage,
    setRowsPerPage,
}) => {
    const { formatMessage, locale } = useIntl()

    // Avoid a layout jump when reaching the last page with empty rows.
    // const emptyRows =
    //     page > 0
    //         ? Math.max(0, (1 + page) * rowsPerPage - studentsData.length)
    //         : 0

    const emptyRows =
        page > 1 ? Math.max(0, rowsPerPage - studentsData.length) : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(1)
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={{ bgcolor: '#E7EDF7' }}>
                        <TableRow>
                            <TableCell>#</TableCell>

                            <TableCell>
                                {formatMessage({ id: 'fullName' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'electiveSubjects' })}
                            </TableCell>

                            <TableCell align="center">
                                {formatMessage({ id: 'os' })}
                            </TableCell>

                            <TableCell align="center">
                                {formatMessage({ id: 'ms' })}
                            </TableCell>

                            <TableCell align="center">
                                {formatMessage({ id: 'his' })}
                            </TableCell>

                            <TableCell align="center">
                                {formatMessage({ id: 'electiveSubjectsCount' }, { count: 1 })}
                            </TableCell>

                            <TableCell align="center">
                                {formatMessage({ id: 'electiveSubjectsCount' }, { count: 2 })}
                            </TableCell>

                            <TableCell align="center">
                                {formatMessage({ id: 'overallScore' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    {studentsData.length ? (
                        <TableBody>
                            {studentsData.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {index + (page - 1) * rowsPerPage + 1}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.firstName} {row?.lastName}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.subjects[3]?.[`title${capitalize(locale)}`]?.slice(0, 3) || ''} - {row?.subjects[4]?.[`title${capitalize(locale)}`]?.slice(0, 3) || ''}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                    >
                                        <Tooltip
                                            placement="top"
                                            title={row?.subjects[0]?.[`title${capitalize(locale)}`] || ''}
                                        >
                                            <span style={{ padding: '12px' }}>
                                                {row?.subjects[0]?.curatorSubject ?
                                                    <span style={{ backgroundColor: '#E7F4EE', padding: '6px 12px', borderRadius: '24px' }}>{row?.subjects[0]?.point} балл</span> :
                                                    row?.subjects[0]?.point
                                                }
                                            </span>
                                        </Tooltip>
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                    >
                                        <Tooltip
                                            placement="top"
                                            title={row?.subjects[1]?.[`title${capitalize(locale)}`] || ''}
                                        >
                                            <span style={{ padding: '12px' }}>
                                                {row?.subjects[1]?.curatorSubject ?
                                                    <span style={{ backgroundColor: '#E7F4EE', padding: '6px 12px', borderRadius: '24px' }}>{row?.subjects[1]?.point} балл</span> :
                                                    row?.subjects[1]?.point
                                                }
                                            </span>
                                        </Tooltip>
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                    >
                                        <Tooltip
                                            placement="top"
                                            title={row?.subjects[2]?.[`title${capitalize(locale)}`] || ''}
                                        >
                                            <span style={{ padding: '12px' }}>
                                                {row?.subjects[2]?.curatorSubject ?
                                                    <span style={{ backgroundColor: '#E7F4EE', padding: '6px 12px', borderRadius: '24px' }}>{row?.subjects[2]?.point} балл</span> :
                                                    row?.subjects[2]?.point
                                                }
                                            </span>
                                        </Tooltip>
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                    >
                                        <Tooltip
                                            placement="top"
                                            title={row?.subjects[3]?.[`title${capitalize(locale)}`] || ''}
                                        >
                                            <span style={{ padding: '12px' }}>
                                                {row?.subjects[3]?.curatorSubject ?
                                                    <span style={{ backgroundColor: '#E7F4EE', padding: '6px 12px', borderRadius: '24px' }}>{row?.subjects[3]?.point} балл</span> :
                                                    row?.subjects[3]?.point
                                                }
                                            </span>
                                        </Tooltip>
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                    >
                                        <Tooltip
                                            placement="top"
                                            title={row?.subjects[4]?.[`title${capitalize(locale)}`] || ''}
                                        >
                                            <span style={{ padding: '12px' }}>
                                                {row?.subjects[4]?.curatorSubject ?
                                                    <span style={{ backgroundColor: '#E7F4EE', padding: '6px 12px', borderRadius: '24px' }}>{row?.subjects[4]?.point} балл</span> :
                                                    row?.subjects[4]?.point
                                                }
                                            </span>
                                        </Tooltip>
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                    >
                                        {row?.totalScore}
                                    </TableCell>
                                </TableRow>
                            ))}

                            {emptyRows > 0 && (
                                <TableRow style={{ height: 53 * emptyRows }}>
                                    <TableCell colSpan={9} />
                                </TableRow>
                            )}
                        </TableBody>
                    ) : (
                        !isFetching && (
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                        colSpan={9}
                                    >
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )
                    )}

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[
                                    5,
                                    10,
                                    25,
                                    { label: 'All', value: -1 },
                                ]}
                                colSpan={9}
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={pageNumber}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                sx={{ border: 0 }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default AnalysisTable
